import { MessageCircle, Feather, Palette, Bluetooth, BatteryCharging, Smartphone } from "lucide-react";

/**
 * SixReasons — mirrors the official Signia India Styletto IX brand structure
 * ("6 reasons to choose Styletto IX") for SEO/keyword parity.
 * Uses semantic H2/H3 hierarchy, real headings, and keyword-rich copy so
 * Google can crawl the same phrase-stack that ranks on signia.net.
 */
const REASONS = [
    {
        icon: MessageCircle,
        title: "Contribute to the conversation",
        body:
            "Signia Integrated Xperience (IX) with RealTime Conversation Enhancement adapts to your conversational environment in real time — so you never lose a word in noisy, multi-speaker groups.",
    },
    {
        icon: Feather,
        title: "The sleek and comfortable choice",
        body:
            "A beautiful ultra-slim SlimRIC design takes up less space behind the ear. Comfortable even with glasses and hats — barely visible in profile.",
    },
    {
        icon: Palette,
        title: "Diverse color options",
        body:
            "Available in seven distinctive colours — from Black Gloss and Cosmic Blue to Snow White and Rose Gold — for a style that reflects the real you.",
    },
    {
        icon: Bluetooth,
        title: "Enhanced connectivity",
        body:
            "Full Bluetooth streaming for iPhone (MFi) and Android (ASHA). Hands-free calling for iPhone. Ready for the future of LE Audio via firmware update.",
    },
    {
        icon: BatteryCharging,
        title: "Powered for all-day conversations",
        body:
            "Lithium-ion rechargeable battery delivers a full day of listening plus streaming. The pocket-sized wireless charging case adds up to 5 additional full charges — perfect for travel.",
    },
    {
        icon: Smartphone,
        title: "Tailored to your hearing",
        body:
            "Fine-tune every setting to your unique hearing profile through the Signia app — with Signia Assistant AI on standby for on-the-fly help, plus My WellBeing daily activity tracking built in.",
    },
];

export default function SixReasons() {
    return (
        <section className="py-16 md:py-20 border-t border-[#E5E7EB]" data-testid="six-reasons-section">
            <div className="container-x">
                <div className="max-w-[720px]">
                    <p className="eyebrow">Why Styletto IX</p>
                    <h2 className="font-serif text-[36px] md:text-[48px] leading-[1.05] mt-3">
                        6 reasons to choose <span className="italic text-[#003E4D]">Styletto IX</span>
                    </h2>
                    <p className="mt-5 text-[15.5px] text-[#4a5568] leading-relaxed">
                        Styletto IX harnesses the full power of Signia Integrated Xperience with
                        <b> RealTime Conversation Enhancement</b> — clinically shown to outperform four
                        top competitor platforms in noisy group settings.<sup>*</sup>
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-10">
                    {REASONS.map((r, i) => {
                        const Icon = r.icon;
                        return (
                            <article
                                key={i}
                                className="p-6 md:p-7 rounded-2xl bg-white border border-black/5 hover:border-[#003E4D]/25 hover:shadow-[0_20px_40px_-24px_rgba(0,0,0,0.15)] transition"
                                data-testid={`reason-${i}`}
                            >
                                <div className="w-11 h-11 rounded-xl bg-[#003E4D]/[0.08] flex items-center justify-center">
                                    <Icon size={20} className="text-[#003E4D]" />
                                </div>
                                <h3 className="font-serif text-[20px] leading-tight mt-5">{r.title}</h3>
                                <p className="text-[13.5px] text-[#4a5568] leading-relaxed mt-2">{r.body}</p>
                            </article>
                        );
                    })}
                </div>

                <p className="mt-8 text-[11.5px] text-[#9CA3AF] max-w-[720px] leading-relaxed">
                    <sup>*</sup> Jensen NS, Wilson C, Kamkar Parsi H, Taylor B. 2023.
                    &ldquo;Improving the signal-to-noise ratio in group conversations with Signia
                    Integrated Xperience and RealTime Conversation Enhancement.&rdquo; Signia
                    White Paper.
                </p>
            </div>
        </section>
    );
}
